/**
 * @fileoverview Route-level permission guard.
 *
 * แก้ปัญหา: user ที่ไม่มี permission ของเมนู ยังสามารถพิมพ์ URL เข้าหน้าได้โดยตรง
 * (เมนูถูกซ่อนแล้วจาก Navbar แต่ route ไม่ได้ตรวจสิทธิ์)
 *
 * วิธีการ: ใช้ "แหล่งความจริงเดียว" (single source of truth) คือ map ของ
 * link -> permission ที่ประกาศไว้แล้วใน global.js (global.menu_admin/customer/
 * warehouse/super_user) มาตรวจสิทธิ์ตอนเข้าถึง route โดยตรง
 *
 * ใช้เป็น layout route ของ react-router v6:
 *   <Route element={<PermissionRoute />}>
 *     <Route path="/ad/dashboard" element={<AdminDashboard />} />
 *     ...
 *   </Route>
 */

import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
// ensure global.menu_* are populated (idempotent import)
import "../global.js";

/**
 * สร้าง map ของ pathname -> permission ที่ต้องใช้ จากนิยามเมนูใน global.js
 * @returns {Object<string,string>}
 */
const buildPermissionMap = () => {
  const map = {};
  const menus = [
    ...(global.menu_admin || []),
    ...(global.menu_customer || []),
    ...(global.menu_warehouse || []),
    ...(global.menu_super_user || []),
  ];
  menus.forEach((item) => {
    if (item.link && item.permission) {
      const path = item.link.split("?")[0]; // ตัด query string ออก
      map[path] = item.permission;
    }
  });
  return map;
};

/**
 * อ่าน profile จาก localStorage (ค่าล่าสุดเสมอ)
 * @returns {Object|null}
 */
const getProfile = () => {
  try {
    return JSON.parse(localStorage.getItem("cm-profile"));
  } catch {
    return null;
  }
};

const PermissionRoute = () => {
  const location = useLocation();
  const token = localStorage.getItem("cm-acc-tk");
  const profile = getProfile();

  // 1) ยังไม่ได้ login -> ไปหน้า login
  if (!token || !profile) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  // 2) บัญชีระดับแพลตฟอร์ม/บริษัท ข้ามการตรวจสิทธิ์ระดับหน้า
  //    (สอดคล้องกับการ special-case ที่มีอยู่เดิมใน Navbar/console)
  if (["super_user", "admin-company"].includes(profile.user_type)) {
    return <Outlet />;
  }

  // 3) ประเภทผู้ใช้ที่ถูกล็อกพอร์ทัล -> เข้าได้เฉพาะพอร์ทัลของตัวเอง แม้ role จะมี permission
  //    (กันกรณีพิมพ์ URL ตรง ๆ หรือถูกกำหนด role ที่มีสิทธิ์กว้างเกินไป)
  //    ช่างเทคนิค = เข้าได้เฉพาะพอร์ทัลแอดมิน ส่วนสิทธิ์ระดับหน้ายังถูกตรวจต่อในข้อ 5
  //    นิยามการล็อกอยู่ที่ global.PORTAL_LOCK_BY_USER_TYPE ที่เดียว (portal/console ใช้ตัวเดียวกัน)
  //    หลุดพอร์ทัลที่ไม่ได้รับอนุญาต -> กลับไปหน้าเลือกพอร์ทัล จะเห็นการ์ดที่เข้าไม่ได้เป็นกุญแจ
  const lock = global.PORTAL_LOCK_BY_USER_TYPE[profile.user_type];
  if (lock) {
    const prefix = Object.keys(global.PORTAL_BY_PREFIX).find((item) => location.pathname.startsWith(item));
    const portal = prefix ? global.PORTAL_BY_PREFIX[prefix] : null;
    if (portal && !lock.portals.includes(portal)) {
      return <Navigate to={lock.fallback} replace />;
    }
  }

  const required = buildPermissionMap()[location.pathname];

  // 4) ไม่มีการประกาศ permission สำหรับ path นี้ -> ปล่อยผ่าน (เช่นหน้าที่ไม่อยู่ในเมนู)
  if (!required) {
    return <Outlet />;
  }

  // 5) ตรวจสิทธิ์จาก permission ของ role
  const perms = profile?.role?.permission || [];
  if (perms.includes(required)) {
    return <Outlet />;
  }

  // 6) ไม่มีสิทธิ์ -> หน้า 403 (ไม่ใช่ 404 เพราะหน้ามีอยู่จริง แค่ role ไม่ถึง)
  //    แยกให้ชัดว่าเป็นปัญหาสิทธิ์ ไม่ใช่ปัญหา route หาย
  return <Navigate to="/forbidden" replace />;
};

export default PermissionRoute;
